import type { WidgetConfig } from './config';
import { addCoupon, getReferralProgram, refreshAuthAndCart } from './api';
import { t } from './i18n';
import { openCartView } from './navigation';
import { isAuthRoutePath, openContextualLogin } from './returnTo';
import { store } from './store';
import {
  forgetReferralCoupon,
  pendingReferralCoupon,
  referralCouponFromSearch,
  rememberReferralCoupon,
  withoutReferralCouponParams,
} from './referralCouponQuery';

let applying = false;
let loginPrompted = false;

function cleanReferralCouponUrl(): void {
  if (typeof window === 'undefined') return;
  const clean = withoutReferralCouponParams(window.location.pathname + window.location.search + window.location.hash);
  window.history.replaceState({}, '', clean);
}

/** Handles the referral coupon email contract after auth state has been refreshed. */
export async function handleReferralCouponEntry(config: WidgetConfig): Promise<void> {
  if (typeof window === 'undefined' || applying) return;
  const fromUrl = referralCouponFromSearch(window.location.search);
  if (fromUrl) rememberReferralCoupon(fromUrl);
  const coupon = fromUrl ?? pendingReferralCoupon();
  if (!coupon) return;

  if (!store.getAuth()) {
    if (!loginPrompted) {
      loginPrompted = true;
      store.addToast(t('referralCoupon.loginRequired'), 'info');
      if (!isAuthRoutePath(window.location.pathname)) {
        openContextualLogin('cart');
      }
    }
    return;
  }

  applying = true;
  try {
    const program = await getReferralProgram(config).catch(() => null);
    const rewardPercent = program?.result?.coupons.find(
      (item) => item.code.trim().toUpperCase() === coupon,
    )?.percent;
    const result = await addCoupon(config, coupon);
    const appliedCoupon = String((result.result as Record<string, unknown> | undefined)?.coupon ?? '').trim().toUpperCase();
    if (result.status !== 200 || appliedCoupon !== coupon) {
      store.addToast(t('referralCoupon.applyFailed'), 'error');
      return;
    }
    forgetReferralCoupon();
    cleanReferralCouponUrl();
    store.addToast(rewardPercent == null
      ? t('referralCoupon.appliedGeneric')
      : t('referralCoupon.applied', { reward: rewardPercent }), 'success');
    await refreshAuthAndCart(config, store);
    openCartView();
  } finally {
    applying = false;
  }
}
